import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
} from "react";
import { MediaMetadata, usePfmPostSocial } from "./use-pfm-post-social";
import type { DraftVariations } from "../types/creator-studio";
import { PostContentPayload } from "../types/post-content";
import {
  MAX_IMAGES_MAP,
  MAX_VIDEOS_MAP,
  PLATFORM_REQUIREMENTS,
  PlatformId,
  X_PREMIUM_MAX_CHARACTERS,
  X_STANDARD_MAX_CHARACTERS,
  urlToFile,
  validatePlatform,
  validateTextContent,
} from "../utils/platform-post-util";

import { BrandDetails } from "../types/add-brand-types";
import { ContentLog } from "../types/dashboard";
import { dispatchWeeklyPlanRefresh } from "../utils/weekly-plan-events";
import { trackEvent } from "../utils/firebase-analytics";

export type HistoryViewMode = "draft" | "scheduled" | "published" | null;

export interface UsePfmStep3Options {
  selectedVariation?: DraftVariations;
  socialPlatforms: ReadonlyArray<{
    id: string;
    name: string;
    icon: string;
  }>;
  brandId: string;
  brandDetails: BrandDetails;
  historyItem?: ContentLog;
  onPostSuccess?: () => void;
  prefilledScheduleDate?: Date;
  prefilledPlatforms?: string[];
  weeklyPlanItemId?: string;
}

interface PlatformMediaState {
  mediaFiles: File[];
  mediaPreviewUrls: string[];
  mediaMetadata: MediaMetadata[];
}

const EMPTY_MEDIA_STATE: PlatformMediaState = {
  mediaFiles: [],
  mediaPreviewUrls: [],
  mediaMetadata: [],
};

const normalizePlatformId = (platform: string): string => {
  const normalized = platform.toLowerCase();
  if (normalized === "linkedin") return "linkedin";
  if (normalized === "x") return "x";
  return normalized;
};

const isTwitterPlatform = (platform: string): boolean => {
  const normalized = normalizePlatformId(platform);
  return normalized === "twitter" || normalized === "x";
};

const createMediaState = (
  media: { type: string; url: string }[] = []
): PlatformMediaState => ({
  mediaFiles: [],
  mediaPreviewUrls: media.map((item) => item.url),
  mediaMetadata: media.map((item) => ({
    url: item.url,
    width: 0,
    height: 0,
    size: 0,
    aspectRatio: 0,
    type: item.type === "video" ? "video" : "image",
    duration: item.type === "video" ? 0 : undefined,
  })),
});

const loadMediaMetadataFromUrl = (
  media: { type: string; url: string }
): Promise<MediaMetadata> =>
  new Promise((resolve) => {
    if (media.type === "video") {
      const video = document.createElement("video");
      video.preload = "metadata";
      video.crossOrigin = "anonymous";
      video.onloadedmetadata = () =>
        resolve({
          url: media.url,
          width: video.videoWidth,
          height: video.videoHeight,
          size: 0,
          aspectRatio:
            video.videoHeight > 0 ? video.videoWidth / video.videoHeight : 0,
          type: "video",
          duration: video.duration,
        });
      video.onerror = () =>
        resolve({
          url: media.url,
          width: 0,
          height: 0,
          size: 0,
          aspectRatio: 0,
          type: "video",
          duration: 0,
        });
      video.src = media.url;
      return;
    }

    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () =>
      resolve({
        url: media.url,
        width: img.width,
        height: img.height,
        size: 0,
        aspectRatio: img.height > 0 ? img.width / img.height : 0,
        type: "image",
      });
    img.onerror = () =>
      resolve({
        url: media.url,
        width: 0,
        height: 0,
        size: 0,
        aspectRatio: 0,
        type: "image",
      });
    img.src = media.url;
  });

const loadMediaMetadataFromUrls = async (
  media: { type: string; url: string }[] = []
): Promise<MediaMetadata[]> => Promise.all(media.map(loadMediaMetadataFromUrl));

const getInitialPlatformCaptions = (
  historyItem: ContentLog | undefined,
  selectedPlatforms: string[],
  fallbackPostBody: string,
  fallbackTweetBody: string
) => {
  const historyContent = historyItem?.content;
  return selectedPlatforms.reduce<Record<string, string>>((acc, platform) => {
    const normalizedPlatform = normalizePlatformId(platform);
    const captionFromMap =
      historyContent?.platformCaptions?.[platform] ??
      historyContent?.platformCaptions?.[normalizedPlatform];
    const captionFromContent = isTwitterPlatform(normalizedPlatform)
      ? historyContent?.threads?.[0] ?? fallbackTweetBody
      : fallbackPostBody;

    acc[platform] = captionFromMap ?? captionFromContent ?? "";
    return acc;
  }, {});
};

export function usePfmStep3(options: UsePfmStep3Options) {
  const {
    selectedVariation,
    brandId,
    socialPlatforms,
    brandDetails,
    historyItem,
    onPostSuccess,
    prefilledScheduleDate,
    prefilledPlatforms,
    weeklyPlanItemId,
  } = options;

  const viewMode: HistoryViewMode = historyItem
    ? (historyItem.status.toLowerCase() as "draft" | "scheduled" | "published")
    : null;

  const {
    postTitle,
    setPostTitle,
    postBody: initialPostBody,
    setPostBody: setInitialPostBody,
    tweetBody: initialTweetBody,
    setTweetBody: setInitialTweetBody,
    scheduleOption,
    setScheduleOption,
    togglePlatform,
    selectedPlatforms,
    useCommonMediaForAllPlatforms,
    setUseCommonMediaForAllPlatforms,
    scheduledDate,
    setScheduledDate,
    createPost,
    updatePost,
    uploadMedia,
    threadEnabled,
    setThreadEnabled,
    threadTweets,
    setThreadTweets,
    isStory,
    setIsStory,
    subreddits,
    setSubreddits,
    searchSubreddit,
  } = usePfmPostSocial(
    brandDetails,
    selectedVariation,
    historyItem?.content,
    prefilledScheduleDate,
    prefilledPlatforms
  );

  const [platformCaptions, setPlatformCaptions] = useState<
    Record<string, string>
  >(() =>
    getInitialPlatformCaptions(
      historyItem,
      selectedPlatforms,
      initialPostBody,
      initialTweetBody
    )
  );
  const [commonMediaState, setCommonMediaState] = useState<PlatformMediaState>(
    () => createMediaState(historyItem?.content?.media ?? [])
  );
  const [platformMediaStates, setPlatformMediaStates] = useState<
    Record<string, PlatformMediaState>
  >(() => {
    const historyContent = historyItem?.content;
    if (!historyContent?.platformMedia) return {};

    return selectedPlatforms.reduce<Record<string, PlatformMediaState>>(
      (acc, platform) => {
        const normalizedPlatform = normalizePlatformId(platform);
        const platformMedia =
          historyContent.platformMedia?.[platform] ??
          historyContent.platformMedia?.[normalizedPlatform] ??
          [];

        acc[platform] = createMediaState(platformMedia);
        return acc;
      },
      {}
    );
  });

  const [activeFormTab, setActiveFormTab] = useState<string>(
    historyItem?.content?.platforms?.[0] ?? "linkedin"
  );
  const [showPlatformSelector, setShowPlatformSelector] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [isLaunching, setIsLaunching] = useState(false);
  const [xLongFormEnabled, setXLongFormEnabled] = useState(false);
  const [isHydratingExistingMedia, setIsHydratingExistingMedia] = useState(
    () =>
      !!historyItem?.content?.media?.length ||
      (!!historyItem?.content?.platformMedia &&
        Object.values(historyItem.content.platformMedia).some(
          (media) => media.length > 0
        ))
  );
  const [toast, setToast] = useState<{
    message: string;
    type: "error" | "warning" | "success";
    id: number;
  } | null>(null);

  const xMaxCharacters = xLongFormEnabled
    ? X_PREMIUM_MAX_CHARACTERS
    : X_STANDARD_MAX_CHARACTERS;

  const getTextValidationOptions = useCallback(
    (platform: string) =>
      isTwitterPlatform(platform)
        ? { maxCharacters: xMaxCharacters }
        : undefined,
    [xMaxCharacters]
  );

  useEffect(() => {
    if (
      selectedPlatforms.length > 0 &&
      !selectedPlatforms.includes(activeFormTab)
    ) {
      setActiveFormTab(selectedPlatforms[0]);
    }
  }, [selectedPlatforms, activeFormTab]);

  useEffect(() => {
    if (!selectedPlatforms.length) return;

    setPlatformCaptions((prev) => {
      const next = { ...prev };
      selectedPlatforms.forEach((platform) => {
        if (next[platform] !== undefined) return;
        const normalizedPlatform = normalizePlatformId(platform);
        const captionFromMap =
          historyItem?.content?.platformCaptions?.[platform] ??
          historyItem?.content?.platformCaptions?.[normalizedPlatform];

        next[platform] =
          captionFromMap ??
          (isTwitterPlatform(platform) ? initialTweetBody : initialPostBody) ??
          "";
      });
      return next;
    });
  }, [historyItem?.content?.platformCaptions, initialPostBody, initialTweetBody, selectedPlatforms]);

  useEffect(() => {
    const historyContent = historyItem?.content;
    if (!historyContent) {
      setIsHydratingExistingMedia(false);
      return;
    }

    const hasCommonMedia = (historyContent.media ?? []).length > 0;
    const hasPlatformMedia = Object.values(historyContent.platformMedia ?? {}).some(
      (media) => media.length > 0
    );

    if (!hasCommonMedia && !hasPlatformMedia) {
      setIsHydratingExistingMedia(false);
      return;
    }

    let cancelled = false;

    const hydrateExistingMedia = async () => {
      setIsHydratingExistingMedia(true);

      const [commonMetadata, platformMetadataEntries] = await Promise.all([
        loadMediaMetadataFromUrls(historyContent.media ?? []),
        Promise.all(
          selectedPlatforms.map(async (platform) => {
            const normalizedPlatform = normalizePlatformId(platform);
            const platformMedia =
              historyContent.platformMedia?.[platform] ??
              historyContent.platformMedia?.[normalizedPlatform] ??
              [];
            const mediaState: PlatformMediaState = {
              mediaFiles: [],
              mediaPreviewUrls: platformMedia.map((item) => item.url),
              mediaMetadata: await loadMediaMetadataFromUrls(platformMedia),
            };

            return [
              platform,
              mediaState,
            ] as const;
          })
        ),
      ]);

      if (cancelled) return;

      setCommonMediaState({
        mediaFiles: [],
        mediaPreviewUrls: (historyContent.media ?? []).map((item) => item.url),
        mediaMetadata: commonMetadata,
      });

      setPlatformMediaStates(
        platformMetadataEntries.reduce<Record<string, PlatformMediaState>>(
          (acc, [platform, mediaState]) => {
            acc[platform] = mediaState;
            return acc;
          },
          {}
        )
      );
      setIsHydratingExistingMedia(false);
    };

    hydrateExistingMedia().catch(() => {
      if (!cancelled) {
        setIsHydratingExistingMedia(false);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [historyItem, selectedPlatforms]);

  useEffect(() => {
    if (!selectedPlatforms.length) return;

    if (!useCommonMediaForAllPlatforms) {
      setPlatformMediaStates((prev) => {
        const next = { ...prev };
        selectedPlatforms.forEach((platform) => {
          if (!next[platform]) {
            next[platform] = {
              mediaFiles: [...commonMediaState.mediaFiles],
              mediaPreviewUrls: [...commonMediaState.mediaPreviewUrls],
              mediaMetadata: [...commonMediaState.mediaMetadata],
            };
          }
        });
        return next;
      });
      return;
    }

    const merged: PlatformMediaState = {
      mediaFiles: [],
      mediaPreviewUrls: [],
      mediaMetadata: [],
    };
    const seen = new Set<string>();
    selectedPlatforms.forEach((platform) => {
      const state = platformMediaStates[platform];
      if (!state) return;
      state.mediaPreviewUrls.forEach((url, index) => {
        if (seen.has(url)) return;
        seen.add(url);
        merged.mediaPreviewUrls.push(url);
        if (state.mediaFiles[index]) merged.mediaFiles.push(state.mediaFiles[index]);
        if (state.mediaMetadata[index]) merged.mediaMetadata.push(state.mediaMetadata[index]);
      });
    });

    const currentSignature = commonMediaState.mediaPreviewUrls.join("|");
    const mergedSignature = merged.mediaPreviewUrls.join("|");
    if (merged.mediaPreviewUrls.length > 0 && mergedSignature !== currentSignature) {
      setCommonMediaState(merged);
    }
  }, [
    useCommonMediaForAllPlatforms,
    selectedPlatforms,
    commonMediaState.mediaFiles,
    commonMediaState.mediaMetadata,
    commonMediaState.mediaPreviewUrls,
    platformMediaStates,
  ]);

  const activeMediaState = useMemo<PlatformMediaState>(() => {
    if (useCommonMediaForAllPlatforms) return commonMediaState;
    return platformMediaStates[activeFormTab] ?? EMPTY_MEDIA_STATE;
  }, [
    activeFormTab,
    commonMediaState,
    platformMediaStates,
    useCommonMediaForAllPlatforms,
  ]);

  const mediaPreviewUrls = activeMediaState.mediaPreviewUrls;
  const mediaMetadata = activeMediaState.mediaMetadata;
  const activePlatformBody = platformCaptions[activeFormTab] ?? "";
  const postBody = isTwitterPlatform(activeFormTab) ? initialPostBody : activePlatformBody;
  const tweetBody = isTwitterPlatform(activeFormTab) ? activePlatformBody : initialTweetBody;

  const setPostBody = useCallback(
    (value: string) => {
      setInitialPostBody(value);
      setPlatformCaptions((prev) => ({
        ...prev,
        [activeFormTab]: value,
      }));
    },
    [activeFormTab, setInitialPostBody]
  );

  const setTweetBody = useCallback(
    (value: string) => {
      setInitialTweetBody(value);
      setPlatformCaptions((prev) => {
        const next = {
          ...prev,
          [activeFormTab]: value,
        };
        if (activeFormTab === "x" && next.twitter === undefined) {
          next.twitter = value;
        }
        if (activeFormTab === "twitter" && next.x === undefined) {
          next.x = value;
        }
        return next;
      });
    },
    [activeFormTab, setInitialTweetBody]
  );

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (
        !target.closest(".platform-selector-dropdown") &&
        !target.closest(".add-platform-btn")
      ) {
        setShowPlatformSelector(false);
      }
    };
    if (showPlatformSelector) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showPlatformSelector]);

  const platformValidations = useMemo(() => {
    const validations: Record<string, { isValid: boolean; errors: string[] }> =
      {};
    selectedPlatforms.forEach((platform) => {
      if (isHydratingExistingMedia) {
        validations[platform] = {
          isValid: true,
          errors: [],
        };
        return;
      }
      const platformReqs = PLATFORM_REQUIREMENTS[platform as PlatformId];
      if (!platformReqs) {
        validations[platform] = {
          isValid: false,
          errors: ["Unknown platform"],
        };
        return;
      }
      const maxMedia = Math.max(
        platformReqs.maxImages + platformReqs.maxVideos,
        10
      );
      const platformMedia = useCommonMediaForAllPlatforms
        ? commonMediaState.mediaMetadata
        : (platformMediaStates[platform]?.mediaMetadata ?? []);
      const enabledMedia = platformMedia.slice(0, maxMedia);
      validations[platform] = validatePlatform(platform, enabledMedia, {
        isStory: platform === "instagram" ? isStory : undefined,
      });
    });
    return validations;
  }, [
    selectedPlatforms,
    useCommonMediaForAllPlatforms,
    commonMediaState.mediaMetadata,
    platformMediaStates,
    isHydratingExistingMedia,
    isStory,
  ]);

  const validationIssues = useMemo(() => {
    const issues: string[] = [];
    if (selectedPlatforms.length === 0) {
      issues.push("Select at least one platform");
    }
    const descriptionRequiredPlatforms = selectedPlatforms.filter(
      (platform) =>
        !isTwitterPlatform(platform) &&
        !(normalizePlatformId(platform) === "instagram" && isStory)
    );
    const hasNonTwitterPlatform = descriptionRequiredPlatforms.length > 0;
    const hasTwitter = selectedPlatforms.some((p) => isTwitterPlatform(p));
    const hasNonTwitterCaption = descriptionRequiredPlatforms.some(
      (platform) => !!platformCaptions[platform]?.trim()
    );
    const hasTwitterCaption = selectedPlatforms.some(
      (platform) =>
        isTwitterPlatform(platform) && !!platformCaptions[platform]?.trim()
    );
    if (hasNonTwitterPlatform && !hasNonTwitterCaption) {
      issues.push("Post description is required");
    }
    if (hasTwitter && !hasTwitterCaption) {
      issues.push("Tweet content is required");
    }
    const requiresTitle = selectedPlatforms.some((p) =>
      ["reddit", "linkedin"].includes(p)
    );
    if (requiresTitle && !postTitle?.trim()) {
      const platforms = selectedPlatforms
        .filter((p) => ["reddit", "linkedin"].includes(p))
        .map((p) => socialPlatforms.find((sp) => sp.id === p)?.name)
        .filter(Boolean)
        .join(", ");
      issues.push(`Post title is required for ${platforms}`);
    }
    if (scheduleOption === "later" && !scheduledDate) {
      issues.push("Scheduled date and time is required");
    }
    if (
      selectedPlatforms.includes("reddit") &&
      (!subreddits || subreddits.length === 0)
    ) {
      issues.push("At least one subreddit is required for Reddit");
    }
    Object.entries(platformValidations).forEach(([platform, validation]) => {
      if (!validation.isValid) {
        const platformName =
          socialPlatforms.find((p) => p.id === platform)?.name ?? platform;
        validation.errors.forEach((error) => {
          issues.push(`${platformName}: ${error}`);
        });
      }
    });

    selectedPlatforms.forEach((platform) => {
      const text = platformCaptions[platform];
      if (text) {
        const textValidation = validateTextContent(
          platform,
          text,
          getTextValidationOptions(platform)
        );
        if (!textValidation.isValid) {
          const platformName =
            socialPlatforms.find((p) => p.id === platform)?.name ?? platform;
          textValidation.errors.forEach((error) => {
            issues.push(`${platformName}: ${error}`);
          });
        }
      }
    });

    return issues;
  }, [
    selectedPlatforms,
    platformValidations,
    platformCaptions,
    postTitle,
    subreddits,
    scheduleOption,
    scheduledDate,
    socialPlatforms,
    isStory,
    getTextValidationOptions,
  ]);

  const isLaunchDisabled = useMemo(
    () => validationIssues.length > 0,
    [validationIssues]
  );

  const getMaxImages = useCallback((platform: string): number => {
    return MAX_IMAGES_MAP[platform] ?? 10;
  }, []);

  const getMaxVideos = useCallback((platform: string): number => {
    return MAX_VIDEOS_MAP[platform] ?? 1;
  }, []);

  const updateActiveMediaState = useCallback(
    (updater: (current: PlatformMediaState) => PlatformMediaState) => {
      if (useCommonMediaForAllPlatforms) {
        setCommonMediaState((prev) => updater(prev));
        return;
      }
      setPlatformMediaStates((prev) => {
        const current = prev[activeFormTab] ?? EMPTY_MEDIA_STATE;
        return { ...prev, [activeFormTab]: updater(current) };
      });
    },
    [activeFormTab, useCommonMediaForAllPlatforms]
  );

  const createMetadataForFiles = useCallback(async (files: File[]) => {
    const metadataPromises = files.map((file) => {
      return new Promise<MediaMetadata>((resolve) => {
        const url = URL.createObjectURL(file);
        const isVideo = file.type.startsWith("video/");

        if (isVideo) {
          const video = document.createElement("video");
          video.preload = "metadata";
          video.onloadedmetadata = () =>
            resolve({
              url,
              width: video.videoWidth,
              height: video.videoHeight,
              size: file.size,
              aspectRatio: video.videoWidth / video.videoHeight,
              type: "video",
              duration: video.duration,
            });
          video.onerror = () =>
            resolve({
              url,
              width: 0,
              height: 0,
              size: file.size,
              aspectRatio: 0,
              type: "video",
              duration: 0,
            });
          video.src = url;
          return;
        }

        const img = new Image();
        img.onload = () =>
          resolve({
            url,
            width: img.width,
            height: img.height,
            size: file.size,
            aspectRatio: img.width / img.height,
            type: "image",
          });
        img.onerror = () =>
          resolve({
            url,
            width: 0,
            height: 0,
            size: file.size,
            aspectRatio: 0,
            type: "image",
          });
        img.src = url;
      });
    });

    return Promise.all(metadataPromises);
  }, []);

  const handleMediaUpload = useCallback(
    async (files: File[]) => {
      if (!files.length) return;
      const metadata = await createMetadataForFiles(files);
      updateActiveMediaState((current) => ({
        mediaFiles: [...current.mediaFiles, ...files],
        mediaPreviewUrls: [
          ...current.mediaPreviewUrls,
          ...metadata.map((m) => m.url),
        ],
        mediaMetadata: [...current.mediaMetadata, ...metadata],
      }));
    },
    [createMetadataForFiles, updateActiveMediaState]
  );

  const removeMedia = useCallback(
    (index: number) => {
      updateActiveMediaState((current) => ({
        mediaFiles: current.mediaFiles.filter((_, i) => i !== index),
        mediaPreviewUrls: current.mediaPreviewUrls.filter((_, i) => i !== index),
        mediaMetadata: current.mediaMetadata.filter((_, i) => i !== index),
      }));
    },
    [updateActiveMediaState]
  );

  const reorderMedia = useCallback(
    (fromIndex: number, toIndex: number) => {
      updateActiveMediaState((current) => {
        const nextFiles = [...current.mediaFiles];
        const nextUrls = [...current.mediaPreviewUrls];
        const nextMetadata = [...current.mediaMetadata];
        const [file] = nextFiles.splice(fromIndex, 1);
        const [url] = nextUrls.splice(fromIndex, 1);
        const [meta] = nextMetadata.splice(fromIndex, 1);
        nextFiles.splice(toIndex, 0, file);
        nextUrls.splice(toIndex, 0, url);
        nextMetadata.splice(toIndex, 0, meta);
        return {
          mediaFiles: nextFiles,
          mediaPreviewUrls: nextUrls,
          mediaMetadata: nextMetadata,
        };
      });
    },
    [updateActiveMediaState]
  );

  const submitPost = useCallback(
    async (isDraft: boolean) => {
      const descriptionRequiredPlatforms = selectedPlatforms.filter(
        (platform) =>
          !isTwitterPlatform(platform) &&
          !(normalizePlatformId(platform) === "instagram" && isStory)
      );
      const hasNonTwitter = descriptionRequiredPlatforms.length > 0;
      const hasTwitter = selectedPlatforms.some((p) => isTwitterPlatform(p));
      const firstNonTwitterCaption = descriptionRequiredPlatforms
        .map((platform) => platformCaptions[platform]?.trim() ?? "")
        .find(Boolean);
      const firstTwitterCaption = selectedPlatforms
        .filter((platform) => isTwitterPlatform(platform))
        .map((platform) => platformCaptions[platform]?.trim() ?? "")
        .find(Boolean);

      if (hasNonTwitter && !firstNonTwitterCaption) {
        setToast({
          message: "Post description is required",
          type: "error",
          id: Date.now(),
        });
        return;
      }
      if (hasTwitter && !firstTwitterCaption) {
        setToast({
          message: "Tweet content is required",
          type: "error",
          id: Date.now(),
        });
        return;
      }
      const requiresTitle = selectedPlatforms.some((p) =>
        ["reddit", "linkedin"].includes(p)
      );
      if (requiresTitle && !postTitle?.trim()) {
        setToast({
          message: "Post title is required for Reddit and LinkedIn",
          type: "error",
          id: Date.now(),
        });
        return;
      }
      if (scheduleOption === "later" && !scheduledDate) {
        setToast({
          message: "Please select a date and time for scheduling",
          type: "error",
          id: Date.now(),
        });
        return;
      }
      if (scheduleOption === "later" && scheduledDate && scheduledDate < new Date()) {
        setToast({
          message: "Scheduled date must be in the future",
          type: "error",
          id: Date.now(),
        });
        return;
      }
      const failedPlatforms = Object.entries(platformValidations).filter(
        ([, v]) => !v.isValid
      );
      if (failedPlatforms.length > 0) {
        const [platform, validation] = failedPlatforms[0];
        const platformName =
          socialPlatforms.find((p) => p.id === platform)?.name ?? platform;
        setToast({
          message: `${platformName} does not meet requirements: ${validation.errors[0]}`,
          type: "error",
          id: Date.now(),
        });
        return;
      }
      const failedTextPlatform = selectedPlatforms.find((platform) => {
        const text = platformCaptions[platform];
        if (!text?.trim()) return false;
        return !validateTextContent(
          platform,
          text,
          getTextValidationOptions(platform)
        ).isValid;
      });
      if (failedTextPlatform) {
        const platformName =
          socialPlatforms.find((p) => p.id === failedTextPlatform)?.name ??
          failedTextPlatform;
        const [error] = validateTextContent(
          failedTextPlatform,
          platformCaptions[failedTextPlatform] ?? "",
          getTextValidationOptions(failedTextPlatform)
        ).errors;
        setToast({
          message: `${platformName}: ${error}`,
          type: "error",
          id: Date.now(),
        });
        return;
      }

      setIsLaunching(true);
      try {
        const uploadMediaForState = async (
          state: PlatformMediaState
        ): Promise<{ type: string; url: string }[]> => {
          const uploadedMedia: { type: string; url: string }[] = [];
          for (let i = 0; i < state.mediaPreviewUrls.length; i++) {
            let fileToUpload: File;
            if (state.mediaFiles[i]) {
              fileToUpload = state.mediaFiles[i];
            } else {
              fileToUpload = await urlToFile(
                state.mediaPreviewUrls[i],
                `media-${i}`,
                i
              );
            }
            const uploaded = await uploadMedia([fileToUpload]);
            if (uploaded) uploadedMedia.push(uploaded);
          }
          return uploadedMedia;
        };

        const commonUploadedMedia = useCommonMediaForAllPlatforms
          ? await uploadMediaForState(commonMediaState)
          : [];

        const uploadedPlatformMedia: Record<
          string,
          { type: string; url: string }[]
        > = {};
        if (!useCommonMediaForAllPlatforms) {
          for (const platform of selectedPlatforms) {
            const mediaState = platformMediaStates[platform] ?? EMPTY_MEDIA_STATE;
            uploadedPlatformMedia[platform] = await uploadMediaForState(mediaState);
          }
        }

        const payload: PostContentPayload = {
          title: postTitle,
          content: hasNonTwitter
            ? firstNonTwitterCaption ?? ""
            : firstTwitterCaption ?? "",
          media: commonUploadedMedia,
          platformMedia: useCommonMediaForAllPlatforms
            ? undefined
            : uploadedPlatformMedia,
          platformCaptions: selectedPlatforms.reduce<Record<string, string>>(
            (acc, platform) => {
              acc[platform] = platformCaptions[platform] ?? "";
              return acc;
            },
            {}
          ),
          useCommonMediaForAllPlatforms,
          scheduled: scheduleOption === "later",
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          scheduledFor:
            scheduleOption === "later" && scheduledDate
              ? scheduledDate.toISOString().replace(/\.\d{3}Z$/, "Z")
              : null,
          platforms: selectedPlatforms,
          draft: isDraft,
          brandId: brandId,
          brandName: brandDetails.brand_name,
          threads:
            hasTwitter && threadEnabled
              ? [firstTwitterCaption ?? "", ...threadTweets.filter((t) => t.trim())]
              : [],
          story: isStory,
          subreddits:
            selectedPlatforms.includes("reddit") && subreddits?.length
              ? subreddits
              : undefined,
          weeklyPlanItemId:
            weeklyPlanItemId ?? historyItem?.content?.weeklyPlanItemId ?? null,
          generationMetadata:
            selectedVariation?.generationMetadata ??
            historyItem?.content?.generationMetadata,
        };
        const response = historyItem
          ? await updatePost(payload, historyItem.post_id, historyItem.id)
          : await createPost(payload);
        if (response) {
          trackEvent("post_submission_completed", {
            action: isDraft
              ? "draft"
              : payload.scheduled
              ? "schedule"
              : historyItem
              ? "update"
              : "publish",
            provider: "postforme",
            platform_count: selectedPlatforms.length,
            has_media:
              commonUploadedMedia.length > 0 ||
              Object.values(uploadedPlatformMedia).some(
                (items) => items.length > 0
              ),
            has_weekly_plan_item: Boolean(payload.weeklyPlanItemId),
          });
          if (weeklyPlanItemId || historyItem?.content?.weeklyPlanItemId) {
            dispatchWeeklyPlanRefresh(brandId);
          }
          const successMessage = isDraft
            ? "Draft saved successfully"
            : payload.scheduled
            ? "Post scheduled successfully"
            : historyItem
            ? "Post updated successfully"
            : "Post submitted for processing";
          setToast({
            message: successMessage,
            type: "success",
            id: Date.now(),
          });
          setTimeout(() => {
            setIsLaunching(false);
            onPostSuccess?.();
          }, 1500);
        } else {
          trackEvent("post_submission_failed", {
            action: isDraft
              ? "draft"
              : payload.scheduled
              ? "schedule"
              : historyItem
              ? "update"
              : "publish",
            provider: "postforme",
            platform_count: selectedPlatforms.length,
          });
          const errorMessage = isDraft
            ? "Failed to save draft"
            : payload.scheduled
            ? "Failed to schedule post"
            : historyItem
            ? "Failed to update post"
            : "Failed to publish post";
          setToast({
            message: errorMessage,
            type: "error",
            id: Date.now(),
          });
          setIsLaunching(false);
        }
      } catch {
        trackEvent("post_submission_failed", {
          action: isDraft
            ? "draft"
            : scheduleOption === "later"
            ? "schedule"
            : historyItem
            ? "update"
            : "publish",
          provider: "postforme",
          platform_count: selectedPlatforms.length,
        });
        setIsLaunching(false);
      }
    },
    [
      selectedPlatforms,
      postTitle,
      scheduleOption,
      scheduledDate,
      platformValidations,
      socialPlatforms,
      brandId,
      brandDetails.brand_name,
      createPost,
      updatePost,
      historyItem,
      selectedVariation?.generationMetadata,
      weeklyPlanItemId,
      uploadMedia,
      threadEnabled,
      threadTweets,
      isStory,
      subreddits,
      platformCaptions,
      useCommonMediaForAllPlatforms,
      commonMediaState,
      platformMediaStates,
      onPostSuccess,
      getTextValidationOptions,
    ]
  );

  const handleLaunch = useCallback(() => submitPost(false), [submitPost]);
  const handleSaveDraft = useCallback(() => submitPost(true), [submitPost]);

  const processFilesForUpload = useCallback(
    (files: File[]) => {
      const platformName = socialPlatforms.find(
        (p) => p.id === activeFormTab
      )?.name;
      const currentImages = activeMediaState.mediaMetadata.filter(
        (m) => m.type === "image"
      ).length;
      const currentVideos = activeMediaState.mediaMetadata.filter(
        (m) => m.type === "video"
      ).length;
      const newImages = files.filter((f) => f.type.startsWith("image/"));
      const newVideos = files.filter((f) => f.type.startsWith("video/"));
      const maxImages = getMaxImages(activeFormTab);
      const maxVideos = getMaxVideos(activeFormTab);

      if (
        newVideos.length > 0 &&
        currentVideos + newVideos.length > maxVideos
      ) {
        if (maxVideos === 0) {
          setToast({
            message: `Videos are not supported for ${platformName}`,
            type: "warning",
            id: Date.now(),
          });
          return;
        }
        const availableVideoSlots = maxVideos - currentVideos;
        if (availableVideoSlots <= 0) {
          setToast({
            message: `Maximum ${maxVideos} ${
              maxVideos === 1 ? "video" : "videos"
            } allowed for ${platformName}`,
            type: "warning",
            id: Date.now(),
          });
          return;
        }
        setToast({
          message: `You can only add ${availableVideoSlots} more ${
            availableVideoSlots === 1 ? "video" : "videos"
          } for ${platformName}`,
          type: "warning",
          id: Date.now(),
        });
        return;
      }

      if (
        newImages.length > 0 &&
        currentImages + newImages.length > maxImages
      ) {
        const availableImageSlots = maxImages - currentImages;
        if (availableImageSlots <= 0) {
          setToast({
            message: `Maximum ${maxImages} ${
              maxImages === 1 ? "image" : "images"
            } allowed for ${platformName}`,
            type: "warning",
            id: Date.now(),
          });
          return;
        }
        setToast({
          message: `You can only add ${availableImageSlots} more ${
            availableImageSlots === 1 ? "image" : "images"
          } for ${platformName}`,
          type: "warning",
          id: Date.now(),
        });
        handleMediaUpload([
          ...newVideos,
          ...newImages.slice(0, availableImageSlots),
        ]);
        return;
      }
      handleMediaUpload(files);
    },
    [
      activeFormTab,
      activeMediaState.mediaMetadata,
      getMaxImages,
      getMaxVideos,
      handleMediaUpload,
      socialPlatforms,
    ]
  );

  const handleFileChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      if (event.target.files?.length) {
        processFilesForUpload(Array.from(event.target.files));
      }
    },
    [processFilesForUpload]
  );

  const handleDrop = useCallback(
    (event: React.DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      if (event.dataTransfer.files?.length) {
        processFilesForUpload(Array.from(event.dataTransfer.files));
      }
    },
    [processFilesForUpload]
  );

  const handleDragOver = useCallback(
    (event: React.DragEvent<HTMLDivElement>) => {
      event.preventDefault();
    },
    []
  );

  const handleImageDragStart = useCallback(
    (e: React.DragEvent<HTMLDivElement>, index: number) => {
      setDraggedIndex(index);
      e.dataTransfer.effectAllowed = "move";
    },
    []
  );

  const handleImageDragOver = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.dataTransfer.dropEffect = "move";
    },
    []
  );

  const handleImageDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>, dropIndex: number) => {
      e.preventDefault();
      e.stopPropagation();
      if (draggedIndex != null && draggedIndex !== dropIndex) {
        reorderMedia(draggedIndex, dropIndex);
      }
      setDraggedIndex(null);
    },
    [draggedIndex, reorderMedia]
  );

  const handleImageDragEnd = useCallback(() => {
    setDraggedIndex(null);
  }, []);

  const renderTextWithHashtags = useCallback((text: string) => {
    const lines = text.split("\n");
    return lines.map((line, lineIndex) =>
      React.createElement(
        React.Fragment,
        { key: lineIndex },
        ...line.split(/(#\w+)/g).map((part, partIndex) => {
          const key = `${lineIndex}-${partIndex}`;
          if (part.startsWith("#")) {
            return React.createElement(
              "span",
              { key, style: { color: "#1d9bf0" } },
              part
            );
          }
          return React.createElement("span", { key }, part);
        }),
        lineIndex < lines.length - 1
          ? React.createElement("br", { key: `${lineIndex}-br` })
          : null
      )
    );
  }, []);

  return {
    postTitle,
    setPostTitle,
    postBody,
    setPostBody,
    tweetBody,
    setTweetBody,
    scheduleOption,
    setScheduleOption,
    togglePlatform,
    selectedPlatforms,
    mediaPreviewUrls,
    mediaMetadata,
    useCommonMediaForAllPlatforms,
    setUseCommonMediaForAllPlatforms,
    handleMediaUpload,
    removeMedia,
    reorderMedia,
    scheduledDate,
    setScheduledDate,
    activeFormTab,
    setActiveFormTab,
    showPlatformSelector,
    setShowPlatformSelector,
    fileInputRef,
    draggedIndex,
    toast,
    setToast,
    platformValidations,
    validationIssues,
    isLaunchDisabled,
    isLaunching,
    xLongFormEnabled,
    setXLongFormEnabled,
    xMaxCharacters,
    handleLaunch,
    handleSaveDraft,
    getMaxImages,
    getMaxVideos,
    handleFileChange,
    handleDragOver,
    handleDrop,
    handleImageDragStart,
    handleImageDragOver,
    handleImageDrop,
    handleImageDragEnd,
    renderTextWithHashtags,
    socialPlatforms,
    viewMode,
    threadEnabled,
    setThreadEnabled,
    threadTweets,
    setThreadTweets,
    isStory,
    setIsStory,
    subreddits,
    setSubreddits,
    searchSubreddit,
  };
}
